import React, { useState } from 'react';
import { Loader2, Sparkles, Mail, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithAuth } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ApolloEnrichPanel = ({ companyId, onEnriched }) => {
  const [searching, setSearching] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [selected, setSelected] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [enriching, setEnriching] = useState(false);

  const search = async () => {
    setSearching(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/crm/companies/${companyId}/apollo/search-decision-makers`);
      setCandidates(data.contacts || []);
      setSelected({});
    } catch (e) {
      toast.error('Apollo search failed for this company');
    } finally {
      setSearching(false);
    }
  };

  const toggle = (apolloId) => setSelected((s) => ({ ...s, [apolloId]: !s[apolloId] }));

  const selectedCandidates = (candidates || []).filter((c) => selected[c.apollo_id]);
  const creditCost = selectedCandidates.filter((c) => !c.already_enriched).length;

  const confirmEnrich = async () => {
    setEnriching(true);
    try {
      for (const c of selectedCandidates) {
        await fetchWithAuth(`${API_URL}/api/crm/companies/${companyId}/apollo/contacts/${c.apollo_id}/reveal`, { method: 'POST' });
      }
      toast.success(`Enriched ${selectedCandidates.length} contact(s)`);
      setConfirming(false);
      setCandidates(null);
      setSelected({});
      onEnriched();
    } catch (e) {
      toast.error('Enrichment failed partway — check CRM contacts for what was saved');
    } finally {
      setEnriching(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`apollo-panel-${companyId}`}>
      {!candidates ? (
        <button onClick={search} disabled={searching} data-testid={`apollo-search-btn-${companyId}`}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90 disabled:opacity-50">
          {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Search Apollo
        </button>
      ) : candidates.length === 0 ? (
        <p className="text-xs text-gray-400" data-testid={`apollo-no-results-${companyId}`}>No decision-makers found on Apollo for this company.</p>
      ) : (
        <div className="space-y-2">
          {candidates.map((c) => (
            <label key={c.apollo_id} data-testid={`apollo-candidate-${c.apollo_id}`}
              className="flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg border border-gray-200 hover:border-[#0A1F44]/30 cursor-pointer">
              <input type="checkbox" checked={!!selected[c.apollo_id]} onChange={() => toggle(c.apollo_id)} data-testid={`apollo-checkbox-${c.apollo_id}`} />
              <span className="font-medium text-[#0A1F44]">{c.contact_name}</span>
              <span className="text-gray-500 truncate">{c.title}</span>
              {c.already_enriched ? (
                <span className="ml-auto flex items-center gap-1 text-emerald-600 font-medium flex-shrink-0" data-testid={`apollo-status-saved-${c.apollo_id}`}>
                  <CheckCircle2 className="h-3 w-3" /> Already in CRM
                </span>
              ) : (
                <span className="ml-auto flex items-center gap-1 text-amber-600 font-medium flex-shrink-0" data-testid={`apollo-status-credit-${c.apollo_id}`}>
                  <Mail className="h-3 w-3" /> Requires 1 credit
                </span>
              )}
            </label>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <button onClick={() => setConfirming(true)} disabled={selectedCandidates.length === 0} data-testid={`apollo-enrich-selected-${companyId}`}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">
              Enrich Selected ({selectedCandidates.length})
            </button>
            <button onClick={() => setCandidates(null)} data-testid={`apollo-cancel-search-${companyId}`} className="text-xs text-gray-400 hover:text-gray-600">
              Cancel
            </button>
          </div>
        </div>
      )}

      {confirming && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid={`apollo-confirm-modal-${companyId}`}>
          <div className="bg-white rounded-xl p-5 max-w-sm shadow-xl">
            <div className="flex items-center gap-2 text-amber-600 mb-2">
              <AlertTriangle className="h-4 w-4" /> <span className="font-semibold text-sm">Confirm Apollo Enrichment</span>
            </div>
            <p className="text-xs text-gray-600 mb-4">
              You've selected <b>{selectedCandidates.length}</b> contact(s). <b>{creditCost}</b> will consume Apollo credits (1 each);{' '}
              {selectedCandidates.length - creditCost} are already saved in Omnia and free.
            </p>
            <div className="flex gap-2">
              <button onClick={confirmEnrich} disabled={enriching} data-testid={`apollo-confirm-btn-${companyId}`}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90 disabled:opacity-50">
                {enriching && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Confirm & Enrich
              </button>
              <button onClick={() => setConfirming(false)} disabled={enriching} data-testid={`apollo-cancel-confirm-${companyId}`}
                className="flex-1 px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
